/**
 * Use the full bleed shadow box hero for content that wrrants the highest level of prominence with more focus on the copy,as opposed to the image.
 *
 * @module views/components/FullBleedShadowBoxHero
 * @memberof -Common
 */
import './FullBleedShadowBoxHero.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Icon from '@ulta/core/components/Icon/Icon';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import Tag from '@ulta/modules/Tag/Tag';

import { constants } from '@ulta/utils/constants/constants';

/**
* Represents a FullBleedShadowBoxHero component
*
* @method
* @param {FullBleedShadowBoxHeroProps} props - React properties passed from composition
* @returns FullBleedShadowBoxHero
*/
export const FullBleedShadowBoxHero = function( props ){
  const { inflection } = useDeviceInflection();
  const fullBleedShadowBoxHeroRef = useRef( null );
  const {
    rootMargin = defaultProps.rootMargin,
    root = defaultProps.root,
    threshold = defaultProps.threshold,
    backgroundImage,
    campaignImage,
    campaignImageMobile,
    tag,
    eyebrow,
    headline,
    headlineTag = defaultProps.headlineTag,
    subheadline,
    action,
    shadowColor
  } = props;
  const fullBleedLink = action && action?.label;

  useIntersectionObserver( fullBleedShadowBoxHeroRef, {
    root: root,
    rootMargin: rootMargin,
    threshold: threshold
  }, handleIntersection( props ) );

  /**
   * Represents a fullBleedContent function
   * This function will take in the data from user and format it for the Full Bleed Shadow Box Hero
   * @method
   * @returns fullBleedContent
   */

  const fullBleedContent = () => {
    return (
      <>
        { ( backgroundImage && campaignImageMobile ) &&
          <div className={
            classNames( 'FullBleedShadowBoxHero__backgroundImage', {
              'FullBleedShadowBoxHero__backgroundImage--noCampaign': !campaignImage
            } )
          }
          >
            <UltaAsset
              imageUrl={ inflection.DESKTOP ? backgroundImage.imageUrl : campaignImageMobile.imageUrl }
              altText={ backgroundImage.altText }
              { ...spreadProps( {
                metaData: backgroundImage.metaData
              } ) }
              bgAnimation={ true }
            />
          </div>
        }
        <GridContainer>
          { campaignImage &&
            <div className='FullBleedShadowBoxHero__campaignImage'>
              <UltaAsset
                imageUrl={ campaignImage.imageUrl }
                altText={ campaignImage.altText }
                { ...spreadProps( {
                  metaData: campaignImage.metaData
                } ) }
              />
            </div>

          }

          <div className={
            classNames( 'FullBleedShadowBoxHero__textSection', {
              'FullBleedShadowBoxHero__textSection--rowPosition': campaignImage
            } )
          }
          >
            { tag &&
              <div className={
                classNames( 'FullBleedShadowBoxHero__tag', {
                  'FullBleedShadowBoxHero__tag--position': campaignImage
                } )
              }
              >
                <Tag
                  style='default'
                  message={ tag }
                />
              </div>
            }
            { eyebrow &&
              <div className={
                classNames( 'FullBleedShadowBoxHero__eyebrow', {
                  'FullBleedShadowBoxHero__eyebrow--position': !tag
                } )
              }
              >
                <Text
                  htmlTag='p'
                  textStyle='eyebrow'
                  textAlign='center'
                >
                  { eyebrow }
                </Text>
              </div>
            }
            <div className={
              classNames( 'FullBleedShadowBoxHero__headline', {
                'FullBleedShadowBoxHero__headline--position': !tag && !eyebrow,
                'FullBleedShadowBoxHero__headline--tagNoEyebrow': tag && !eyebrow
              } )
            }
            >
              <Text
                htmlTag={ headlineTag }
                textStyle='title-4'
                textAlign='center'
              >
                { headline }
              </Text>
            </div>
            { subheadline &&
              <div className='FullBleedShadowBoxHero__Subheadline'>
                <Text
                  htmlTag='p'
                  textStyle='subtitle-1'
                  textAlign='center'
                >
                  { subheadline }
                </Text>
              </div>
            }
            { action?.label &&
              <div className='FullBleedShadowBoxHero__action'>
                <Text
                  textStyle='title-6'
                  htmlTag='span'
                  color='black'
                  textAlign='center'
                >
                  { action.label }
                  {
                    <Icon
                      size='m'
                      name='ArrowForward'
                      aria-hidden={ true }
                    />
                  }
                </Text>
              </div>
            }
          </div>
          <div className={
            classNames( 'FullBleedShadowBoxHero__colorBox', {
              'FullBleedShadowBoxHero__colorBox--rowPosition': campaignImage,
              [`FullBleedShadowBoxHero__colorBox--${shadowColor}`]: shadowColor
            } )
          }
          >
          </div>
        </GridContainer>
      </>
    );
  };

  if( !headline || !backgroundImage ){
    return null;
  }

  return (
    <div className='FullBleedShadowBoxHero'
      ref={ fullBleedShadowBoxHeroRef }
    >
      <div className='FullBleedShadowBoxHero__contentContainer'>
        { fullBleedLink &&
          <Link_Huge
            action={ action }
          >
            { fullBleedContent() }
          </Link_Huge>
        }
        { !fullBleedLink &&
          <>
            { fullBleedContent() }
          </>
        }
      </div>
    </div>
  );
};

/**
* Property type definitions
* @typedef FullBleedShadowBoxHeroProps
* @type {object}
* @property {object} backgroundImage - Sets the backgroundImage
* @property {object} campaignImage - Sets the campaignImage logo
* @property {string} tag - Sets the tag
* @property {string} eyebrow - Sets the eyebrow text
* @property {string} headline - Sets the headline
* @property {string} headlineTag - Sets the headlineTag text
* @property {string} subheadline - Sets the subheadline
* @property {object} action - Sets the action
* @property {String} shadowColor - Sets the box shadow Color
* @property {object} campaignImageMobile - Sets the background Image for mobile view
*/
export const propTypes = {
  backgroundImage: PropTypes.object,
  campaignImage: PropTypes.object,
  tag: PropTypes.string,
  eyebrow: PropTypes.string,
  headline: PropTypes.string,
  headlineTag: PropTypes.string,
  subheadline: PropTypes.string,
  action: PropTypes.object,
  shadowColor: PropTypes.string,
  campaignImageMobile: PropTypes.object
};

/**
 * Default values for passed properties
 * @type object
 * @property {string} headlineTag - Default headlineTag is h2
 */
export const defaultProps = {
  headlineTag: 'h2',
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

FullBleedShadowBoxHero.defaultProps = defaultProps;
FullBleedShadowBoxHero.propTypes = propTypes;

export default FullBleedShadowBoxHero;